import { API_BASE_URL as API_BASE } from '../config/api';
import { createApiResponse } from '../models';
import { createAuthenticatedApiClient } from './authenticatedApiClient';
import { getStoredUserId } from './authSession';

const apiClient = createAuthenticatedApiClient({
  baseURL: `${API_BASE}/api/v1`,
});

export const riskAnalysisService = {
  /**
   * Perform comprehensive risk analysis for a company
   * @param {Object} request - Risk analysis request parameters
   * @param {string} request.ticker - Company ticker (required)
   * @param {string} [request.companyName] - Company name
   * @param {number} [request.daysBack=30] - Days to look back for news
   * @param {number} [request.maxNewsArticles=50] - Max news articles to analyze
   * @param {boolean} [request.includeIndustryProfile=true] - Include industry analysis
   * @param {boolean} [request.enableAIAnalysis=true] - Enable AI analysis
   * @param {boolean} [request.enableSentimentAnalysis=true] - Enable sentiment analysis
   * @param {boolean} [request.saveToDatabase=true] - Save to database
   * @param {boolean} [request.updateCompanyProfile=true] - Update company profile
   * @param {number} [request.confidenceThreshold=0.7] - Confidence threshold
   * @param {number} [request.minRelevanceScore=0.1] - Min relevance score
   * @param {string[]} [request.focusRiskCategories] - Focus on specific risk categories
   * @param {string} [request.analysisContext] - Analysis context
   * @param {string} [request.requestId] - Request ID for tracking
   * @param {string} [request.userId] - User ID
   * @param {string} [request.requestTimestamp] - Request timestamp
   * @returns {Promise<Object>} Risk analysis response
   */
  async analyzeRisk(request) {
    try {
      const response = await apiClient.post('risk-analysis/analyze', request);
      const apiResponse = createApiResponse(response.data);

      if (apiResponse.success) {
        return apiResponse;
      }
      // Backend may return 200 with analysis in .data or at top level (analysisId) but success: false
      const analysisData = apiResponse.data ?? (response.data?.analysisId ? response.data : null);
      if (response.status === 200 && analysisData) {
        return createApiResponse({ success: true, data: analysisData, message: apiResponse.message || 'Risk analysis completed' });
      }
      const msg = apiResponse.message || response.data?.message || 'Risk analysis failed';
      return createApiResponse({ success: false, message: msg });
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to perform risk analysis';
      return createApiResponse({ success: false, message });
    }
  },

  _getUserId() {
    return getStoredUserId() || undefined;
  },

  /**
   * Get company details including latest risk analysis.
   * With userId, returns global or this user's user-added company.
   * @param {string} ticker - Company ticker symbol
   * @param {string} [userId] - User ID so user-added companies resolve for owner (defaults to localStorage)
   * @returns {Promise<Object>} Company response with risk analysis
   */
  async getCompanyWithRiskAnalysis(ticker, userId) {
    try {
      const uid = userId ?? this._getUserId();
      const params = uid ? { userId: uid } : {};
      const response = await apiClient.get(`/companies/${ticker}`, { params });
      const apiResponse = createApiResponse(response.data);
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch company with risk analysis'
      });
    }
  },

  /**
   * Check if cached risk analysis is available and still valid (within 15 minutes)
   * @param {Object} company - Company object with risk analysis data
   * @returns {Object} Cache status information
   */
  checkCacheStatus(company) {
    if (!company || !company.riskAnalysisTimestamp) {
      return {
        hasCache: false,
        isExpired: true,
        ageMinutes: null,
        cacheAge: null
      };
    }

    const analysisTime = new Date(company.riskAnalysisTimestamp);
    const now = new Date();
    const ageMinutes = Math.floor((now - analysisTime) / (1000 * 60));
    const isExpired = ageMinutes > 15;

    return {
      hasCache: true,
      isExpired,
      ageMinutes,
      cacheAge: analysisTime.toLocaleString()
    };
  },

  /**
   * Get cached risk analysis if available and not expired
   * @param {string} ticker - Company ticker symbol
   * @param {string} [userId] - User ID so user-added companies resolve
   * @returns {Promise<Object>} Cached risk analysis or null if not available/expired
   */
  async getCachedRiskAnalysis(ticker, userId) {
    try {
      const uid = userId ?? this._getUserId();
      const companyResponse = await this.getCompanyWithRiskAnalysis(ticker, uid);
      if (!companyResponse.success || !companyResponse.data) {
        return null;
      }
      const company = companyResponse.data;
      const cacheStatus = this.checkCacheStatus(company);
      if (!cacheStatus.hasCache || cacheStatus.isExpired) {
        return null;
      }
      return {
        company,
        cacheStatus,
        riskAnalysis: company.latestRiskAnalysis
      };
    } catch (_) {
      return null;
    }
  },

  /**
   * Perform risk analysis with cache-first strategy
   * @param {string} ticker - Company ticker symbol
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} Risk analysis result
   */
  async performRiskAnalysisWithCache(ticker, options = {}) {
    try {
      const uid = options.userId ?? this._getUserId();
      const cachedResult = await this.getCachedRiskAnalysis(ticker, uid);
      if (cachedResult && !cachedResult.cacheStatus.isExpired) {
        return {
          success: true,
          data: cachedResult.riskAnalysis,
          fromCache: true,
          cacheAge: cachedResult.cacheStatus.ageMinutes,
          company: cachedResult.company
        };
      }

      const analysisRequest = {
        ticker,
        companyName: options.companyName,
        daysBack: options.daysBack || 30,
        maxNewsArticles: options.maxNewsArticles || 50,
        includeIndustryProfile: options.includeIndustryProfile !== false,
        enableAIAnalysis: options.enableAIAnalysis !== false,
        enableSentimentAnalysis: options.enableSentimentAnalysis !== false,
        saveToDatabase: options.saveToDatabase !== false,
        updateCompanyProfile: options.updateCompanyProfile !== false,
        confidenceThreshold: options.confidenceThreshold || 0.7,
        minRelevanceScore: options.minRelevanceScore || 0.1,
        focusRiskCategories: options.focusRiskCategories,
        analysisContext: options.analysisContext,
        requestId: options.requestId || `RISK_ANALYSIS_${ticker}_${Date.now()}`,
        userId: options.userId,
        requestTimestamp: options.requestTimestamp || new Date().toISOString()
      };

      const analysisResponse = await this.analyzeRisk(analysisRequest);
      
      if (analysisResponse.success) {
        return {
          ...analysisResponse,
          fromCache: false,
          cacheAge: 0
        };
      }

      return analysisResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.message || 'Failed to perform risk analysis'
      });
    }
  },

  /**
   * Get risk analysis summary for display
   * @param {Object} riskAnalysis - Risk analysis data
   * @returns {Object} Formatted summary
   */
  getRiskAnalysisSummary(riskAnalysis) {
    if (!riskAnalysis) {
      return null;
    }

    return {
      overallRiskScore: riskAnalysis.overallRiskScore || 0,
      supplyChainRiskScore: riskAnalysis.supplyChainRiskScore || 0,
      industryRiskScore: riskAnalysis.industryRiskScore || 0,
      newsSentimentScore: riskAnalysis.newsSentimentScore || 0,
      analysisConfidence: riskAnalysis.analysisConfidence || 0,
      dataQualityScore: riskAnalysis.dataQualityScore || 0,
      analyzedNewsCount: riskAnalysis.analyzedNewsCount || 0,
      positiveNewsCount: riskAnalysis.positiveNewsCount || 0,
      negativeNewsCount: riskAnalysis.negativeNewsCount || 0,
      neutralNewsCount: riskAnalysis.neutralNewsCount || 0,
      keyFindings: riskAnalysis.keyFindings || [],
      recommendations: riskAnalysis.recommendations || [],
      riskTrends: riskAnalysis.riskTrends || [],
      individualRiskScores: riskAnalysis.individualRiskScores || []
    };
  },

  /**
   * Get risk score color coding
   * @param {number} score - Risk score (0-100)
   * @returns {Object} Color classes
   */
  getRiskScoreColor(score) {
    if (score >= 80) return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
    if (score >= 60) return { text: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' };
    if (score >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' };
    return { text: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
  },

  /**
   * Get confidence level color coding
   * @param {number} confidence - Confidence score (0-1)
   * @returns {Object} Color classes
   */
  getConfidenceColor(confidence) {
    if (confidence >= 0.8) return { text: 'text-green-600', bg: 'bg-green-50' };
    if (confidence >= 0.6) return { text: 'text-yellow-600', bg: 'bg-yellow-50' };
    return { text: 'text-red-600', bg: 'bg-red-50' };
  },

  /**
   * Get impact level color coding
   * @param {string} impactLevel - Impact level (High, Medium, Low)
   * @returns {Object} Color classes
   */
  getImpactLevelColor(impactLevel) {
    switch (impactLevel?.toLowerCase()) {
      case 'high': return { text: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
      case 'medium': return { text: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300' };
      case 'low': return { text: 'text-green-700', bg: 'bg-green-100', border: 'border-green-300' };
      default: return { text: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' };
    }
  }
};
